<template>
  <v-menu
    :id="id"
    ref="menu"
    :location="location"
    eager
    :content-props="{ 'data-belongs-to': id }"
    :z-index="zIndex"
    :viewport-margin="viewportMargin"
    :min-width="menuWidth === 'auto' ? 'auto' : undefined"
  >
    <template #activator="{ props }">
      <slot name="activator" v-bind="{ props: getProps(props) }" />
    </template>

    <div
      class="menu-content"
      :class="{
        'overflow-hidden': overflow === 'hidden',
        'overflow-auto': overflow === 'auto',
        'overflow-scroll': overflow === 'scroll',
        'w-fit min-w-full': menuWidth === 'fit',
        'w-full': menuWidth === 'auto',
      }"
    >
      <slot />
    </div>
  </v-menu>
</template>

<script lang="ts" setup>
import { ref } from 'vue';

import { menuDefaultZIndex } from '../lib/z-index';
import type { Anchor, IUIMenuExpose } from './ui-menu-types';

interface Props {
  location?: Anchor;
  overflow?: 'hidden' | 'auto' | 'scroll';
  menuWidth?: 'auto' | 'fit';
  /** Z-index for the menu overlay. Use a higher value when inside a modal so the menu appears above it. */
  zIndex?: number;
  /** Minimum distance the menu keeps from the viewport edge. Lower this when the activator sits intentionally close to the edge. */
  viewportMargin?: number;
}

withDefaults(defineProps<Props>(), {
  location: 'bottom end',
  overflow: 'hidden',
  menuWidth: 'fit',
  zIndex: menuDefaultZIndex,
  viewportMargin: 12,
});

const id = `ui-menu-${Math.random().toString(36).slice(2, 10)}`;
const menu = ref();

function close() {
  menu.value?.activatorEl?.click();
}

function getProps<T extends object>(props: T) {
  return {
    ...props,
    'aria-owns': id,
  };
}

defineExpose<IUIMenuExpose>({ close });
</script>

<style scoped lang="scss">
:deep(.v-list) {
  padding-top: 0;
  padding-bottom: 0;
}

.menu-content {
  max-height: 100vh;
}
</style>
